import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/**
 * LYNE "Peekaboo Pet Tote" purchase panel.
 *
 * Owns design/size/quantity selection and add-to-cart. The theme's own variant-picker stays in
 * the DOM (visually hidden) and is driven from here, so the gallery, price block, `?variant=`
 * deep links and sold-out states behave exactly as on every other product page.
 *
 * The two-tote price is produced by a server-side automatic discount. Nothing here changes what
 * the customer is charged; the figures shown are only a preview of it.
 */
class LynePetTote extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    const data = JSON.parse(this.querySelector('[data-lyne-tote-data]')?.textContent || '{}');
    this.variants = data.variants || [];
    this.designIndex = data.designIndex ?? -1;
    this.sizeIndex = data.sizeIndex ?? -1;
    this.pairPricing = data.pairPricing || {};

    this.section = this.closest('.shopify-section');
    this.bar = document.querySelector(`lyne-pet-tote-bar[data-for="${this.id}"]`);

    this.designButtons = Array.from(this.querySelectorAll('[data-design]'));
    this.sizeButtons = Array.from(this.querySelectorAll('[data-size]'));
    this.offerButtons = Array.from(this.querySelectorAll('[data-qty]'));
    this.secondPanel = this.querySelector('[data-second]');
    this.secondDesign = this.querySelector('[data-second-design]');
    this.secondSize = this.querySelector('[data-second-size]');
    this.addButton = this.querySelector('[data-add]');
    this.summary = this.querySelector('[data-summary]');
    this.designName = this.querySelector('[data-design-name]');

    const selected = this.variants.find((variant) => variant.id === data.selectedId) || this.variants[0];
    this.design = this.designIndex >= 0 ? selected?.options[this.designIndex] : null;

    // A size is only pre-selected when the shopper arrived on an explicit variant URL. The URL is
    // the only reliable signal: `request.query_string` comes back empty on cached renders, even
    // though Liquid itself resolved the deep-linked variant.
    const deepLinked = this.#variantFromUrl();
    this.size = deepLinked ? deepLinked.options[this.sizeIndex] : null;
    if (deepLinked && this.designIndex >= 0) this.design = deepLinked.options[this.designIndex];

    this.qty = 1;

    for (const button of this.designButtons) {
      button.addEventListener('click', () => this.#chooseDesign(button.dataset.design));
      button.addEventListener('keydown', (event) => this.#radioKeys(event, this.designButtons));
    }
    for (const button of this.sizeButtons) {
      button.addEventListener('click', () => this.#chooseSize(button.dataset.size));
      button.addEventListener('keydown', (event) => this.#radioKeys(event, this.sizeButtons));
    }
    for (const button of this.offerButtons) {
      button.addEventListener('click', () => this.#chooseQty(Number(button.dataset.qty)));
      button.addEventListener('keydown', (event) => this.#radioKeys(event, this.offerButtons));
    }
    this.secondTouched = false;
    if (this.secondDesign && this.design) this.secondDesign.value = this.design;
    this.secondDesign?.addEventListener('change', () => {
      this.secondTouched = true;
      this.refresh();
    });
    this.secondSize?.addEventListener('change', () => this.refresh());
    this.addButton?.addEventListener('click', () => this.submit());
    this.bar?.querySelector('[data-bar-add]')?.addEventListener('click', () => this.submit());
    this.querySelector('[data-fit-open]')?.addEventListener('click', () => this.#openFitGuide());

    // Keep in step if anything else on the page changes the variant.
    this.section?.addEventListener(StandardEvents.productSelect, (event) => {
      event.promise
        ?.then((result) => {
          const id = Number(result?.detail?.variantId);
          const known = this.variants.find((variant) => variant.id === id);
          if (known && this.designIndex >= 0) {
            this.design = known.options[this.designIndex];
            this.refresh();
          }
        })
        .catch(() => {})
        .finally(() => this.#sealThemePicker());
    });

    this.#sealThemePicker();

    this.storyImage = document.querySelector('[data-story-image]');
    try {
      this.storyImages = JSON.parse(document.querySelector('[data-story-images]')?.textContent || '{}');
    } catch (error) {
      this.storyImages = {};
    }

    this.#watchStickyBar();
    this.refresh();
  }

  /** @returns {object | undefined} the variant named by `?variant=`, when it is one of ours */
  #variantFromUrl() {
    const id = Number(new URLSearchParams(window.location.search).get('variant'));
    return this.variants.find((variant) => variant.id === id);
  }

  /** Roving arrow-key movement inside a group of role="radio" buttons. */
  #radioKeys(event, group) {
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    const step = keys[event.key];
    if (!step) return;
    event.preventDefault();
    const usable = group.filter((button) => !button.disabled);
    const index = usable.indexOf(event.currentTarget);
    const next = usable[(index + step + usable.length) % usable.length];
    next?.focus();
    next?.click();
  }

  #chooseDesign(value) {
    if (!value || value === this.design) return;
    this.design = value;
    // Bag 2 follows bag 1 until the shopper picks something else for it themselves.
    if (this.secondDesign && !this.secondTouched) this.secondDesign.value = value;
    this.#pushToThemePicker();
    this.refresh();
  }

  #chooseSize(value) {
    const variant = this.findVariant(this.design, value);
    if (!variant || !variant.available) return;
    this.size = value;
    this.#pushToThemePicker();
    this.refresh();
  }

  #chooseQty(qty) {
    this.qty = qty;
    if (this.secondPanel) this.secondPanel.hidden = qty !== 2;
    if (qty === 2 && this.secondDesign && !this.secondTouched) this.secondDesign.value = this.design;
    this.refresh();
  }

  /**
   * The theme's picker is only a mechanism here, not a control: it stays out of the tab order and
   * out of the accessibility tree, so the cards above are the single set of controls on the page.
   * It is re-sealed after every morph, which replaces the element.
   */
  #sealThemePicker() {
    const picker = this.section?.querySelector('variant-picker');
    if (!picker || picker.inert) return;
    picker.inert = true;
    picker.setAttribute('aria-hidden', 'true');
  }

  /**
   * Check the theme's hidden radios and let its variant-picker do the rest: gallery, price
   * block, availability and history are all its job, not ours.
   */
  #pushToThemePicker() {
    const picker = this.section?.querySelector('variant-picker');
    if (!picker) return;
    this.#sealThemePicker();

    /** @param {number} index @param {string | null} value */
    const check = (index, value) => {
      if (index < 0 || value == null) return null;
      const fieldset = picker.querySelector(`fieldset[data-fieldset-index="${index}"]`);
      const input = fieldset?.querySelector(`input[value="${CSS.escape(value)}"]`);
      if (!input || input.checked) return input || null;
      input.checked = true;
      return input;
    };

    // Without a chosen size, keep whichever size the theme already holds so the gallery still
    // resolves to a real variant — the add-to-cart button stays blocked until one is picked.
    const changed = [check(this.designIndex, this.design), check(this.sizeIndex, this.size)].filter(Boolean);
    const last = changed[changed.length - 1];
    if (last) last.dispatchEvent(new Event('change', { bubbles: true }));

    // The picker writes `?variant=` on every change. Drop it again while no size is chosen, so a
    // reload or a shared link never silently pre-selects one.
    setTimeout(() => this.#syncUrl(), 60);
  }

  #syncUrl() {
    const url = new URL(window.location.href);
    const variant = this.size ? this.findVariant(this.design, this.size) : null;
    if (variant) {
      url.searchParams.set('variant', String(variant.id));
    } else {
      url.searchParams.delete('variant');
    }
    if (url.href !== window.location.href) history.replaceState({}, '', url.toString());
  }

  /** @param {string | null} design @param {string | null} size */
  findVariant(design, size) {
    return this.variants.find(
      (variant) =>
        (this.designIndex < 0 || variant.options[this.designIndex] === design) &&
        (this.sizeIndex < 0 || variant.options[this.sizeIndex] === size)
    );
  }

  /** @returns {{items: Array<{id: number, quantity: number}>, problem: string, focus: string}} */
  resolve() {
    const first = this.findVariant(this.design, this.size);
    if (!this.size || !first) return { items: [], problem: '', focus: 'size' };
    if (!first.available) return { items: [], problem: 'That size is sold out in this design.', focus: 'size' };

    const picks = [first];
    if (this.qty === 2) {
      const design = this.secondDesign ? this.secondDesign.value : this.design;
      const size = this.secondSize ? this.secondSize.value : this.size;
      if (!size) return { items: [], problem: '', focus: 'second' };
      const second = this.findVariant(design, size);
      if (!second || !second.available) {
        return { items: [], problem: 'That second bag is sold out. Please pick another design or size.', focus: 'second' };
      }
      picks.push(second);
    }

    const counts = new Map();
    for (const variant of picks) counts.set(variant.id, (counts.get(variant.id) || 0) + 1);
    return { items: Array.from(counts, ([id, quantity]) => ({ id, quantity })), problem: '', focus: '' };
  }

  /** The cart total these choices will produce, as money strings. */
  #pairFigures() {
    const first = this.findVariant(this.design, this.size) || this.#anyVariant(this.design);
    const design = this.secondDesign ? this.secondDesign.value : this.design;
    const size = this.secondSize ? this.secondSize.value : this.size;
    const second = this.findVariant(design, size) || this.#anyVariant(design) || first;
    if (!first || !second) return null;
    return this.pairPricing[String(first.price + second.price)] || null;
  }

  /** @param {string | null} design */
  #anyVariant(design) {
    return (
      this.variants.find((variant) => variant.options[this.designIndex] === design && variant.available) ||
      this.variants.find((variant) => variant.options[this.designIndex] === design)
    );
  }

  refresh() {
    const single = this.findVariant(this.design, this.size) || this.#anyVariant(this.design);
    const pair = this.#pairFigures();

    for (const button of this.designButtons) {
      const chosen = button.dataset.design === this.design;
      button.setAttribute('aria-checked', String(chosen));
      button.tabIndex = chosen ? 0 : -1;
      const soldOut = !this.variants.some(
        (variant) => variant.options[this.designIndex] === button.dataset.design && variant.available
      );
      button.disabled = soldOut;
      button.classList.toggle('is-sold-out', soldOut);
    }

    for (const button of this.sizeButtons) {
      const chosen = button.dataset.size === this.size;
      button.setAttribute('aria-checked', String(chosen));
      button.tabIndex = chosen || (!this.size && button === this.sizeButtons[0]) ? 0 : -1;
      const variant = this.findVariant(this.design, button.dataset.size);
      button.disabled = !variant || !variant.available;
    }

    for (const button of this.offerButtons) {
      const chosen = Number(button.dataset.qty) === this.qty;
      button.setAttribute('aria-checked', String(chosen));
      button.tabIndex = chosen ? 0 : -1;
    }

    if (this.designName && this.design) {
      this.designName.textContent = this.design.replace(/\s*Canvas Bag$/i, '');
    }

    // The lifestyle band sits in its own section, so the theme's morph never reaches it.
    const story = this.storyImages?.[this.design];
    if (this.storyImage && story && this.storyImage.getAttribute('src') !== story.src) {
      this.storyImage.src = story.src;
      this.storyImage.srcset = story.srcset;
      this.storyImage.alt = story.alt || '';
    }

    const singleTotal = this.querySelector('[data-single-total]');
    if (singleTotal && single) singleTotal.textContent = single.priceMoney;

    const pairTotal = this.querySelector('[data-pair-total]');
    const pairEach = this.querySelector('[data-pair-each]');
    const pairSaving = this.querySelector('[data-pair-saving]');
    if (pair) {
      if (pairTotal) pairTotal.textContent = pair.totalMoney;
      if (pairEach) pairEach.textContent = `${pair.eachMoney} each · mix designs`;
      if (pairSaving) pairSaving.textContent = `Save ${pair.savingMoney} compared with buying separately`;
    }

    if (this.secondSize) {
      for (const option of Array.from(this.secondSize.options)) {
        if (!option.value) continue;
        const design = this.secondDesign ? this.secondDesign.value : this.design;
        const variant = this.findVariant(design, option.value);
        option.disabled = !variant || !variant.available;
      }
    }

    const { items, problem, focus } = this.resolve();
    const ready = items.length > 0;
    const price = this.qty === 2 ? pair?.totalMoney : single?.priceMoney;

    let label;
    if (ready) {
      label = this.qty === 2 ? `Add 2 to bag — ${price}` : `Add to bag — ${price}`;
    } else if (focus === 'second') {
      label = 'Choose a size for bag 2';
    } else {
      label = 'Choose a size';
    }
    if (this.addButton) this.addButton.textContent = label;

    const barAdd = this.bar?.querySelector('[data-bar-add]');
    const barPrice = this.bar?.querySelector('[data-bar-price]');
    if (barAdd) barAdd.textContent = ready ? (this.qty === 2 ? 'Add 2 to bag' : 'Add to bag') : 'Choose a size';
    if (barPrice && price) barPrice.textContent = price;

    this.#say(problem || this.#selectionText(), problem ? 'error' : '');
  }

  #selectionText() {
    const design = this.design ? this.design.replace(/\s*Canvas Bag$/i, '') : '';
    if (!this.size) return 'Choose the size that fits your pet.';
    const sizeLabel = this.size === 'S' ? 'Small' : this.size === 'M' ? 'Medium' : this.size;
    if (this.qty !== 2) return `${design} · ${sizeLabel}`;
    const design2 = (this.secondDesign ? this.secondDesign.value : this.design).replace(/\s*Canvas Bag$/i, '');
    const size2 = this.secondSize ? this.secondSize.value : this.size;
    const sizeLabel2 = size2 === 'S' ? 'Small' : size2 === 'M' ? 'Medium' : size2 || 'choose a size';
    return `${design} · ${sizeLabel} and ${design2} · ${sizeLabel2}`;
  }

  /** @param {string} message @param {string} state */
  #say(message, state) {
    if (!this.summary) return;
    this.summary.textContent = message;
    if (state) this.summary.dataset.state = state;
    else delete this.summary.dataset.state;
  }

  #openFitGuide() {
    const guide = document.getElementById('lyne-tote-fit-guide') || document.querySelector('[data-lyne-fit-guide]');
    const details = guide?.querySelector('details') || guide?.closest('details');
    if (details) details.open = true;
    (details || guide)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    details?.querySelector('summary')?.focus();
  }

  /** Reveal the mobile bar only while the real button is out of view, and reserve its height. */
  #watchStickyBar() {
    if (!this.bar || !this.addButton) return;
    const observer = new IntersectionObserver(
      (entries) => {
        this.bar.hidden = entries[0].isIntersecting;
        this.#reserveBarSpace();
      },
      { threshold: 0 }
    );
    observer.observe(this.addButton);
    window.addEventListener('resize', () => this.#reserveBarSpace(), { passive: true });
  }

  #reserveBarSpace() {
    if (!this.bar) return;
    const visible = !this.bar.hidden && getComputedStyle(this.bar).display !== 'none';
    const height = visible ? Math.ceil(this.bar.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--lyne-tote-bar-space', `${height}px`);
  }

  async fetchCart() {
    const cartItems = document.querySelector('cart-items-component');
    if (cartItems && typeof cartItems.fetchCartData === 'function') {
      await customElements.whenDefined('cart-items-component');
      return cartItems.fetchCartData();
    }
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    return response.json();
  }

  async submit() {
    const { items, problem, focus } = this.resolve();

    if (!items.length) {
      if (focus === 'second') {
        this.#say(problem || 'Please choose a size for bag 2.', 'error');
        this.secondSize?.focus();
        this.secondPanel?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      this.#say(problem || 'Please choose Small or Medium.', 'error');
      const target = this.sizeButtons.find((button) => !button.disabled) || this.sizeButtons[0];
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target?.focus();
      return;
    }

    if (this.addButton.dataset.busy === 'true') return;
    this.addButton.dataset.busy = 'true';
    this.addButton.disabled = true;

    const quantity = items.reduce((total, item) => total + item.quantity, 0);
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => element instanceof HTMLElement && element.dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const result = await response.json();
      const cart = await this.fetchCart();
      const detail = {
        items: cart.items,
        source: 'product-form-component',
        sourceId: this.id,
        itemCount: quantity,
        productId: this.dataset.productId,
      };

      if (result.status) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { ...detail, didError: true },
        });
        this.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );
        this.#say(result.description || result.message || 'That could not be added. Please try again.', 'error');
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { ...detail, sections: result.sections, didError: false },
      });
      this.#say(quantity === 2 ? 'Two totes added to your bag.' : 'Added to your bag.', '');
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' })
      );
      this.#say('Something went wrong. Please try again.', 'error');
    } finally {
      this.addButton.dataset.busy = 'false';
      this.addButton.disabled = false;
      this.refresh();
    }
  }
}

if (!customElements.get('lyne-pet-tote')) {
  customElements.define('lyne-pet-tote', LynePetTote);
}
